/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import './CKEditor5Preview.css';

import type { CKEditor5, EditorType } from '@ckeditor/ckeditor5-metadata';
import { type ReactNode, useCallback, useRef, useState, useContext, type RefObject } from 'react';

import { useCKEditor5CdnScripts } from '../../hooks/useCKEditor5CdnScripts/useCKEditor5CdnScripts';
import { useDimensions } from '../../hooks/useDimensions/useDimensions';
import { useIframeToAppSynchronization } from '../../hooks/useIframeToAppSynchronization/useIframeToAppSynchronization';
import { useAsyncEffect } from '../../hooks/useAsyncEffect/useAsyncEffect';

import { useMemoizedState } from '../../hooks/useMemoizedState/useMemoizedState';

import type { EditorPlugins } from '../../types/EditorPlugins';
import { isDecoupledEditor } from '../../utils/isDecoupledEditor/isDecoupledEditor';
import { isPluginLoaded } from '../../utils/isPluginLoaded/isPluginLoaded';
import { loadEditor } from '../../utils/loadEditor/loadEditor';
import { requiresCommentsContainer } from '../../utils/requiresCommentsContainer/requiresCommentsContainer';
import { requiresDocumentOutlineContainer } from '../../utils/requiresDocumentOutlineContainer/requiresDocumentOutlineContainer';
import { requiresMinimapContainer } from '../../utils/requiresMinimapContainer/requiresMinimapContainer';
import { requiresPresenceListContainer } from '../../utils/requiresPresenceListContainer/requiresPresenceListContainer';
import { requiresRevisionHistoryContainers } from '../../utils/requiresRevisionHistoryContainers/requiresRevisionHistoryContainers';
import { getPresets, type PresetView } from '../../utils/getPresets/getPresets';
import { defaultLanguage, type EditorLanguage } from '../LanguageSelection/languages';
import { PresenceListMock } from './PresenceListMock/PresenceListMock';
import type { CKEditor5PreviewProps } from './types';
import {
	EDITOR_ANNOTATIONS_SIDEBAR_IDENTIFIER,
	EDITOR_CONTAINER_IDENTIFIER,
	EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER,
	EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER,
	EDITOR_MINIMAP_SIDEBAR_IDENTIFIER,
	EDITOR_OUTLINE_SIDEBAR_IDENTIFIER,
	EDITOR_PRESENCE_LIST_IDENTIFIER,
	EDITOR_REVISION_HISTORY_EDITOR_IDENTIFIER,
	EDITOR_REVISION_HISTORY_IDENTIFIER,
	EDITOR_REVISION_HISTORY_SIDEBAR_IDENTIFIER,
	EDITOR_SOURCE_IDENTIFIER,
	EDITOR_WORD_COUNT
} from '../../constants/identifiers';
import { ConditionalWrapper } from '../../utils/ConditionalWrapper/ConditionalWrapper';
import { prepareEditorConfigForPreview } from '../../utils/prepareEditorConfigForPreview/prepareEditorConfigForPreview';
import { GTMContext } from '../../contexts/GTMContext';
import { GTM_EVENT_EDITOR_BLUR, GTM_EVENT_EDITOR_FOCUS } from '../../constants/GTMEventNames';
import { cleanUpEditorArtifacts } from '../../utils/cleanUpEditorArtifacts/cleanUpEditorArtifacts';
import { requiresWordCountPlugin } from '../../utils/requiresWordCountPlugin/requiresWordCountPlugin';
import { getRenderKey } from '../../utils/getRenderKey/getRenderKey';
import { getEditorContainerClassNames } from '../../utils/getEditorContainerClassNames/getEditorContainerClassNames';

const MARGIN = 20;
const PRESENCE_LIST_HEIGHT = 130;
const HORIZONTAL_MARGIN_TO_PREVENT_SCROLL = 4;

export function CKEditor5Preview( {
	licenseKey,
	locationOrigin,
	editorPlugins: initialEditorPlugins = [],
	editorType: initialEditorType = 'ClassicEditor',
	toolbarOptions: initialToolbarOptions
}: CKEditor5PreviewProps ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );

	const presetsCallback = useCallback( getPresets, [] );
	const presets = presetsCallback();

	const [ isCKEditor5Loaded ] = useCKEditor5CdnScripts();
	const [ preset, setPreset ] = useState<PresetView>( presets.find( p => p.name === 'Collaborative Document Editor' )! );

	const [ editorDimensionsElement, setEditorDimensionsElement ] = useState<HTMLElement | null>( null );
	const [ windowHeight, setWindowHeight ] = useState( 0 );

	// The editor properties act as a key for the editor container. To avoid unnecessary re-renderings,
	// store updated values only if they have been changed.
	//
	// See #683, #684.
	const [ editorType, setEditorType ] = useMemoizedState<EditorType>( initialEditorType );
	const [ editorLanguage, setEditorLanguage ] = useMemoizedState<EditorLanguage>( defaultLanguage );
	const [ editorPlugins, setEditorPlugins ] = useMemoizedState<EditorPlugins>( initialEditorPlugins );
	const [ toolbarOptions, setToolbarOptions ] = useMemoizedState( initialToolbarOptions || preset.toolbarOptions );

	const editorContainer = useRef<HTMLDivElement>( null );
	const decoupledToolbarElement = useRef<HTMLDivElement>( null );
	const decoupledMenuBarElement = useRef<HTMLDivElement>( null );
	const sourceElement = useRef<HTMLDivElement>( null );
	const outlineSidebar = useRef<HTMLDivElement>( null );
	const annotationsSidebar = useRef<HTMLDivElement>( null );
	const minimapSidebar = useRef<HTMLDivElement>( null );
	const revisionHistoryContainer = useRef<HTMLDivElement>( null );
	const revisionHistoryEditor = useRef<HTMLDivElement>( null );
	const revisionHistorySidebar = useRef<HTMLDivElement>( null );
	const wordCountElement = useRef<HTMLDivElement>( null );
	const [ isFullscreen, setIsFullscreen ] = useState( false );

	const { width: editorWidth, height: editorHeight } = useDimensions( editorDimensionsElement );

	useIframeToAppSynchronization( {
		editorWidth: editorWidth + 2 * MARGIN + HORIZONTAL_MARGIN_TO_PREVENT_SCROLL,
		editorHeight: editorHeight + 4 * MARGIN,
		isFullscreen,
		locationOrigin,
		onEditorLanguageChange: setEditorLanguage,
		onEditorTypeChange: setEditorType,
		onPresetChange: presetName => setPreset( presets.find( p => p.name === presetName )! ),
		onEditorPluginsChange: setEditorPlugins,
		onToolbarOptionsChange: setToolbarOptions,
		onWindowHeightChange: setWindowHeight
	} );

	const {
		result: activeEditor,
		error: ckeditor5Error,
		isLoading
	} = useAsyncEffect(
		async () => {
			// Avoid rendering twice using the same `sourceElement`. See: #516.
			if ( sourceElement.current && 'ckeditorInstance' in sourceElement.current ) {
				return sourceElement.current.ckeditorInstance as CKEditor5.Editor;
			}

			if ( !isCKEditor5Loaded ) {
				return;
			}

			const editorConfig = await prepareEditorConfigForPreview(
				{ editorPlugins, editorType, editorLanguage, licenseKey, preset, toolbarOptions },
				{
					presenceList: null,
					editorContainer: editorContainer.current!,
					outlineSidebar: outlineSidebar.current!,
					annotationsSidebar: annotationsSidebar.current!,
					minimapSidebar: minimapSidebar.current!,
					revisionHistoryContainer: revisionHistoryContainer.current!,
					revisionHistoryEditor: revisionHistoryEditor.current!,
					revisionHistorySidebar: revisionHistorySidebar.current!
				}
			);

			const loadedEditor = await loadEditor(
				{ editorType, editorConfig, toolbarOptions, editorPlugins },
				{ sourceElement, decoupledToolbarElement, decoupledMenuBarElement, wordCountElement }
			);

			loadedEditor.editing.view.document.on( 'focus', () => triggerGTMEvent( { event: GTM_EVENT_EDITOR_FOCUS } ) );
			loadedEditor.editing.view.document.on( 'blur', () => triggerGTMEvent( { event: GTM_EVENT_EDITOR_BLUR } ) );

			if ( isPluginLoaded( loadedEditor, 'Fullscreen' ) ) {
				const fullscreenCommand = loadedEditor.commands.get( 'toggleFullscreen' )!;

				fullscreenCommand.on( 'change:value', ( evt, name, isFullscreenEventValue ) => {
					setIsFullscreen( isFullscreenEventValue );
				} );
			}

			return loadedEditor;
		},
		async () => {
			// To hide balloon panels that may still be displayed at the time of destroying the editor,
			// but also other artifacts created by CKEditor 5 features.
			cleanUpEditorArtifacts();

			if ( !activeEditor ) {
				return;
			}

			await activeEditor.destroy();
		},
		[ isCKEditor5Loaded, editorType, editorPlugins, editorLanguage, preset, editorContainer, toolbarOptions ]
	);

	useAsyncEffect(
		async () => {
			if ( !activeEditor ) {
				return;
			}

			// To exit from the Revision History preview back to the editing.
			if ( isPluginLoaded( activeEditor, 'RevisionHistory' ) ) {
				const RevisionHistoryPlugin = activeEditor.plugins.get( 'RevisionHistory' ) as any;
				const _viewerEditor: CKEditor5.Editor | null = RevisionHistoryPlugin._viewerEditor;

				if ( _viewerEditor ) {
					await ( activeEditor.config.get( 'revisionHistory.closeRevisionViewerCallback' ) as Function ).call( activeEditor );
				}
			}

			// To exit from the full-screen mode.
			if ( isPluginLoaded( activeEditor, 'Fullscreen' ) ) {
				const fullscreenCommand = activeEditor.commands.get( 'toggleFullscreen' )!;

				if ( fullscreenCommand.value ) {
					fullscreenCommand.execute();
				}
			}
		},
		async () => {
			// No destructor.
		},
		[ activeEditor, editorPlugins ]
	);

	if ( ckeditor5Error ) {
		window.parent.postMessage(
			{
				error: ckeditor5Error,
				details: { editorType, editorPlugins, editorLanguage, toolbarOptions },
				isCKEditor5BuilderEvent: true
			},
			locationOrigin
		);
	}

	const hasDocumentOutline = requiresDocumentOutlineContainer( editorPlugins );
	const hasAnnotations = requiresCommentsContainer( editorPlugins );
	const hasMinimap = requiresMinimapContainer( editorPlugins );
	const isDecoupled = isDecoupledEditor( editorType );

	const hasAdditionalContainers = hasDocumentOutline || hasAnnotations || hasMinimap;
	const windowHeightWithPresenceList = windowHeight - PRESENCE_LIST_HEIGHT;
	const windowHeightWithWordCount = requiresWordCountPlugin( editorPlugins ) ?
		windowHeightWithPresenceList - 30 :
		windowHeightWithPresenceList;

	// The placement of the word count container depends on the selected plugins (sidebar containers) and the editor type.
	//
	// When rendering the decoupled editor, we always want to add the container below the "page", because
	// it is not a part of "page" (the editable that looks like A4), but belongs to the document.
	//
	// When the selected plugins require additional containers, the word count element is inserted next to the editable,
	// to avoid CSS complexity when it comes to placement (grid).
	const shouldRenderWordCountBelowEditable = !isDecoupled && hasAdditionalContainers;

	return (
		<div
			ref={ setEditorDimensionsElement }
			className='main-container'
			style={ {
				opacity: isLoading ? '0' : '1',
				transition: isLoading ? 'none' : 'opacity 0.1s ease-in-out 0.1s',
				paddingLeft: `${ MARGIN }px`,
				paddingRight: `${ MARGIN }px`,
				marginTop: `${ 2 * MARGIN }px`,
				minHeight: `${ windowHeight }px`,
				maxHeight: `${ windowHeight }px`
			} }
		>
			{ requiresPresenceListContainer( editorPlugins ) && (
				<div className='presence' data-snippet-selector={ EDITOR_PRESENCE_LIST_IDENTIFIER }>
					<PresenceListMock/>
				</div>
			) }

			<div
				ref={ editorContainer }
				key={ getRenderKey( [
					editorPlugins.join( '-' ),
					JSON.stringify( toolbarOptions ),
					editorType,
					editorLanguage
				] ) }
				className={ getEditorContainerClassNames( editorType, editorPlugins ) }
				data-snippet-selector={ EDITOR_CONTAINER_IDENTIFIER }
			>
				{ isDecoupled && toolbarOptions.isMenuBarUsed && (
					<div
						ref={ decoupledMenuBarElement }
						data-snippet-selector={ EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER }
						className='editor-container__menu-bar'
					/>
				) }
				{ isDecoupled && (
					<div
						ref={ decoupledToolbarElement }
						data-snippet-selector={ EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER }
						className='editor-container__toolbar'
					/>
				) }

				{ /* Add the minimap wrapper when the Minimap plugin is available.*/ }
				<ConditionalWrapper
					condition={ hasMinimap }
					wrapper={ children => (
						<div className='editor-container__minimap-wrapper'>
							{ children }
							<div
								className={ 'editor-container__sidebar editor-container__sidebar_minimap' }
								ref={ minimapSidebar }
								data-snippet-selector={ EDITOR_MINIMAP_SIDEBAR_IDENTIFIER }
							/>
						</div>
					) }
				>
					{ /* Add the editor wrapper if an editor needs any sidebar.*/ }
					<ConditionalWrapper
						condition={ isDecoupled || hasDocumentOutline || hasAnnotations || hasMinimap }
						wrapper={ children => (
							<div
								style={ {
									minHeight: windowHeightWithWordCount + 'px',
									maxHeight: windowHeightWithWordCount + 'px'
								} }
								className='editor-container__editor-wrapper'
							>
								{ children }
							</div>
						) }
					>
						{ hasDocumentOutline && (
							<div
								className={ 'editor-container__sidebar' }
								ref={ outlineSidebar }
								data-snippet-selector={ EDITOR_OUTLINE_SIDEBAR_IDENTIFIER }
							/>
						) }

						<div className='editor-container__editor'>
							<div
								ref={ sourceElement }
								data-snippet-selector={ EDITOR_SOURCE_IDENTIFIER }
								data-skip-children-in-snippets={ true }
							/>
							{ requiresWordCountPlugin( editorPlugins ) && shouldRenderWordCountBelowEditable && (
								<WordCountContainer wordCountRef={ wordCountElement }/>
							) }
						</div>

						{ hasAnnotations && (
							<div
								className={ 'editor-container__sidebar' }
								ref={ annotationsSidebar }
								data-snippet-selector={ EDITOR_ANNOTATIONS_SIDEBAR_IDENTIFIER }
							/>
						) }
					</ConditionalWrapper>
				</ConditionalWrapper>
				{ requiresWordCountPlugin( editorPlugins ) && !shouldRenderWordCountBelowEditable && (
					<WordCountContainer wordCountRef={ wordCountElement }/>
				) }
			</div>

			{ requiresRevisionHistoryContainers( editorPlugins ) && (
				<div
					className='revision-history'
					ref={ revisionHistoryContainer }
					data-snippet-selector={ EDITOR_REVISION_HISTORY_IDENTIFIER }
				>
					<div className='revision-history__wrapper'>
						<div
							ref={ revisionHistoryEditor }
							className='revision-history__editor'
							data-snippet-selector={ EDITOR_REVISION_HISTORY_EDITOR_IDENTIFIER }
						/>
						<div
							ref={ revisionHistorySidebar }
							className='revision-history__sidebar'
							data-snippet-selector={ EDITOR_REVISION_HISTORY_SIDEBAR_IDENTIFIER }
						/>
					</div>
				</div>
			) }
		</div>
	);
}

function WordCountContainer( { wordCountRef }: { wordCountRef: RefObject<HTMLDivElement> } ) {
	return (
		<div data-snippet-selector={ EDITOR_WORD_COUNT } ref={ wordCountRef } className={ 'editor_container__word-count' }>
		</div>
	);
}
